// components/Nav.tsx
import { Link } from 'react-router-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { Button, buttonVariants } from './custom/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from '@/lib/utils';
import useCheckActiveNav from '@/hooks/use-check-active-nav';
import { SideLink } from '@/data/sidelinks';

interface NavProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly isCollapsed: boolean;
  readonly links: SideLink[];
  readonly closeNav: () => void;
}

export default function Nav({
  links,
  isCollapsed,
  className,
  closeNav,
}: NavProps) {
  const renderLink = ({ sub, ...rest }: SideLink) => {
    const key = `${rest.title}-${rest.href}`;
    if (isCollapsed && sub)
      return (
        <NavLinkIconDropdown
          {...rest}
          sub={sub}
          key={key}
          closeNav={closeNav}
        />
      );

    if (isCollapsed)
      return <NavLinkIcon {...rest} key={key} closeNav={closeNav} />;

    if (sub)
      return (
        <NavLinkDropdown {...rest} sub={sub} key={key} closeNav={closeNav} />
      );

    return <NavLink {...rest} key={key} closeNav={closeNav} />;
  };

  return (
    <div
      data-collapsed={isCollapsed}
      className={cn(
        'group border-b bg-background py-2 transition-[max-height,padding] duration-500 data-[collapsed=true]:py-2 md:border-none',
        className
      )}
    >
      <TooltipProvider delayDuration={0}>
        <nav className="grid gap-1 group-[[data-collapsed=true]]:justify-center group-[[data-collapsed=true]]:px-2">
          {links?.map(renderLink)}
        </nav>
      </TooltipProvider>
    </div>
  );
}

interface NavLinkProps extends SideLink {
  readonly subLink?: boolean; // NOSONAR
  readonly closeNav: () => void;
}

function NavLink({
  title,
  icon,
  label,
  href,
  closeNav,
  subLink = false,
}: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav();
  // Explicitly reference subLink in a local variable
  const isSubLink = subLink;
  return (
    <Link
      to={href}
      onClick={closeNav}
      className={cn(
        buttonVariants({
          variant: 'ghost',
          size: 'sm',
        }),
        'h-12 justify-start text-wrap rounded-none px-6',
        isSubLink && 'h-10 w-full border-l border-l-slate-500 px-2'
      )}
      aria-current={checkActiveNav(href) ? 'page' : undefined}
    >
      <div className="mr-2">{icon}</div>
      {title}
      {label && (
        <div className="ml-2 rounded-lg bg-primary px-1 text-[0.625rem] text-primary-foreground">
          {label}
        </div>
      )}
    </Link>
  );
}

/**
 * UPDATED NavLinkDropdown
 *
 * For items with sub-links, we now use CollapsibleTrigger’s asChild prop to wrap the trigger
 * in a react-router-dom Link. This makes the parent clickable (so its href is followed)
 * without changing your structure, CSS, outline, or layout.
 */
function NavLinkDropdown({
  title,
  icon,
  label,
  href,
  sub,
  closeNav,
}: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav();
  const isChildActive = !!sub?.find((s) => checkActiveNav(s.href));

  return (
    <Collapsible defaultOpen={isChildActive}>
      <CollapsibleTrigger asChild>
        <Link
          to={href}
          onClick={closeNav}
          className={cn(
            buttonVariants({
              variant: 'ghost',
              size: 'sm',
            }),
            'group h-12 w-full justify-start rounded-none px-6'
          )}
          aria-current={checkActiveNav(href) ? 'page' : undefined}
        >
          <div className="mr-2">{icon}</div>
          {title}
          {label && (
            <div className="ml-2 rounded-lg bg-primary px-1 text-[0.625rem] text-primary-foreground">
              {label}
            </div>
          )}
          <span className={cn('ml-auto transition-all group-data-[state="open"]:-rotate-180')}>
            <IconChevronDown stroke={1} />
          </span>
        </Link>
      </CollapsibleTrigger>
      <CollapsibleContent className="collapsibleDropdown" asChild>
        <ul>
          {sub!?.map((sublink) => (
            <li key={sublink.title + sublink.href} className="my-1 ml-8">
              {sublink.sub ? (
                <NavLinkDropdown {...sublink} closeNav={closeNav} />
              ) : (
                <NavLink {...sublink} closeNav={closeNav} subLink={true} />
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function NavLinkIcon({ title, icon, label, href, closeNav }: NavLinkProps) {
  // Unused checkActiveNav removed as the icon link only needs to display a tooltip.
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Link
          to={href}
          onClick={closeNav}
          className={cn(
            buttonVariants({
              variant: 'ghost',
              size: 'icon',
            }),
            'h-12 w-12'
          )}
        >
          {icon}
          <span className="sr-only">{title}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="flex items-center gap-4">
        {title}
        {label && (
          <span className="ml-auto text-muted-foreground">{label}</span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function NavLinkIconDropdown({ title, icon, label, sub, closeNav }: NavLinkProps) {
  // Removed unused checkActiveNav and isChildActive as they were not needed for rendering.
  return (
    <DropdownMenu>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12"
            >
              {icon}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-4">
          {title}{' '}
          {label && (
            <span className="ml-auto text-muted-foreground">{label}</span>
          )}
          <IconChevronDown size={18} className="-rotate-90 text-muted-foreground" />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="right" align="start" sideOffset={4}>
        <DropdownMenuLabel>
          {title} {label ? `(${label})` : ''}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sub!?.map(({ title, icon, label, href, sub }) => (
          <DropdownMenuItem key={title + href} asChild>
            <Link
              to={href}
              onClick={closeNav}
              className={''}
            >
              {icon} <span className="ml-2 max-w-52 text-wrap">{title}</span>
              {label && <span className="ml-auto text-xs">{label}</span>}
              {sub && (
                <DropdownMenu>
                  <DropdownMenuContent side="right">
                    {sub?.map(({ title, href, icon }) => (
                      <DropdownMenuItem key={title + href}>
                        <Link to={href} onClick={closeNav}>
                          {icon} <span>{title}</span>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
